import { toInteger, toString } from 'lodash-es';
import {
  AERIAL_TRAM_ROUTE_ID,
  FEEDS,
  TRIMET_GTFS_AGENCY_IDS,
  MAX_SHORT_NAME,
  STREETCAR_ROUTE_IDS,
  OTP2_BIKETOWN_NAME,
  CTRAN_FEED_ID,
  CTRAN_VINE_ROUTE_IDS,
} from '#/shared/constants';
import { BIKETOWN_COMPANY_1, BIKETOWN_COMPANY_2 } from '#/map/constants';
import { getDisplayNameFromProvider } from '#/shared/utils/agency-utils';
import { addZeroWidthSpaceAfterSlash } from '#/shared/utils/string';
import { parseGtfsId } from '#/shared/utils/gtfs-id';

/**
 * Checks if a route is the Portland Aerial Tram
 * @param feedId {string} The feed ID of the route
 * @param routeId {string|number} The route ID
 * @return {boolean} True if this is the aerial tram route
 */
export const isAerialTram = (feedId, routeId) => {
  // Note: routeId can be either string or number depending on where it comes from
  // in the data pipeline, so we normalize to number for comparison
  return feedId === FEEDS.TRIMET && toInteger(routeId) === AERIAL_TRAM_ROUTE_ID;
};

/**
 * Checks if a route is a Portland Streetcar
 * @param feedId {string} The feed ID of the route
 * @param routeId {string|number} The route ID
 * @return {boolean} True if this is a streetcar route
 */
export const isStreetcar = (feedId, routeId) => {
  return (
    feedId === FEEDS.TRIMET && STREETCAR_ROUTE_IDS.includes(routeId.toString())
  );
};

/**
 * Gets the appropriate agency ID for TRIMET sub-agencies (tram/streetcar)
 * @param feedId {string} The feed ID of the route
 * @param routeId {string|number} The route ID
 * @return {string} The agency ID to use ('TRIMET:TRAM', 'TRIMET:PSC', or original feedId)
 */
export const getTrimetSubAgencyId = (feedId, routeId) => {
  if (feedId !== FEEDS.TRIMET) {
    return feedId;
  }

  if (isAerialTram(feedId, routeId)) {
    return TRIMET_GTFS_AGENCY_IDS.AerialTram;
  }

  if (isStreetcar(feedId, routeId)) {
    return TRIMET_GTFS_AGENCY_IDS.Streetcar;
  }

  return feedId;
};

/**
 * Checks if a leg or route is a CTRAN Vine offering.
 *
 * Accepts two shapes:
 *  - Leg object:   { agencyId: "CTRAN:C-TRAN", routeId: "CTRAN:050" }
 *  - Route object: { feedId: "CTRAN", id: "050" }
 *
 * @param {Object} input A leg or route object
 * @return {boolean} True if this is a CTRAN Vine route
 */
export const isVine = input => {
  if (!input) return false;

  // Route objects carry feedId directly; leg objects carry it namespaced inside agencyId
  // in a feedId:agencyName format
  const feedId = input.feedId ?? parseGtfsId(input.agencyId)?.feedId;

  // Route objects carry the route ID in id; leg objects carry it namespaced inside routeId
  const routeId = input.id?.toString() ?? parseGtfsId(input.routeId)?.id;

  return feedId === CTRAN_FEED_ID && CTRAN_VINE_ROUTE_IDS.includes(routeId);
};

/**
 * @param routeNumber {number} ID for the route for which an short name will
 * be returned
 * @param summaryFormat {boolean} If `true` lines that have a color associated
 * with them will return an empty string as their short name
 * @return {string}
 */
export const getShortNameFromRouteNumber = (
  routeNumber,
  summaryFormat = false
) => {
  switch (toInteger(routeNumber)) {
    case 2:
      return 'FX2';
    // MAX lines: Red, Blue, Mall Shuttle, Yellow, Green, Red, Orange
    case 90:
    case 100:
    case 150:
    case 190:
    case 200:
    case 290:
      return summaryFormat ? '' : MAX_SHORT_NAME;
    // WES commuter Rail
    case 203:
      return summaryFormat ? '' : 'WES';
    // Aerial Tram
    case AERIAL_TRAM_ROUTE_ID:
      return 'AT';
    // Streetcar - NS Line
    case 193:
      return 'NS';
    // Streetcar - A Loop
    case 194:
      return 'A';
    // Streetcar - B Loop
    case 195:
      return 'B';
    // Streetcar - S Line
    case 196:
      return 'S';
    default:
      return toString(routeNumber);
  }
};

/**
 * Creates a human-friendly description for a route combining short and long names
 *
 * @param {import('#/types/Routes').Route} [route]
 * @param {Object} [options]
 * @param {boolean} [options.includeAgencyName]
 * If `true`, prepends the route's agency name when available.
 * @param {boolean} [options.includeThirdPartyAgencyName]
 * If `true` and `includeAgencyName` is `false`, prepends the agency name only
 * for routes operated by non-TriMet agencies.
 * @returns {string}
 */
export const createRouteDescription = (
  route,
  { includeAgencyName = false, includeThirdPartyAgencyName = false } = {}
) => {
  // If route is null, undefined, or an empty object, return empty string.
  if (!route || Object.keys(route).length === 0) return '';

  const agencyName =
    includeAgencyName || includeThirdPartyAgencyName
      ? getDisplayNameFromProvider({
          ...route,
          includeTrimet: includeAgencyName,
        })
      : null;

  let descriptionString = null;
  const { routeShortName, routeLongName, fullRouteDescription, desc } = route;

  if (fullRouteDescription) {
    // If Trimet fullRouteDescription is provided, return it directly
    descriptionString = fullRouteDescription;
    // If shortName is not empty
  } else if (routeShortName && routeShortName.trim() !== '') {
    // Check if this route is a CTran Vine Route
    if (isVine(route)) {
      descriptionString = routeLongName;
      // Check if shortName is already at the beginning of longName
    } else if (routeLongName && routeLongName.startsWith(routeShortName)) {
      descriptionString = routeLongName;
    } else {
      // Append shortName and longName with a hyphen
      descriptionString = `${routeShortName}-${routeLongName || desc || ''}`;
    }
  } else {
    // If shortName is empty, just return longName or fallback to desc
    descriptionString = routeLongName || desc || '';
  }

  return `${agencyName ? `${agencyName} ` : ''}${addZeroWidthSpaceAfterSlash(
    descriptionString
  )}`;
};

/**
 * Helper function to return display names for rental companies
 * Some companies, like Biketown, have names that aren't meaningful to users
 * so we need to remap them to meaningful display names
 */
export function getRentalCompanyDisplayName(company) {
  const alternateBiketownNames = [OTP2_BIKETOWN_NAME, BIKETOWN_COMPANY_2];
  if (alternateBiketownNames.includes(company)) {
    return BIKETOWN_COMPANY_1;
  }
  return company;
}
